  import React, { useRef, useState } from 'react';
  import { FaPen, FaUser, FaEnvelope, FaPhone, FaVenusMars, FaBirthdayCake, FaInfoCircle, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
  import { MdPhotoCamera } from 'react-icons/md';
  import BottomNav from '../common/BottomNav';
  import AppHeader from "../common/AppHeader";
  import {
    uploadProfileImageApi,
    updateUserProfileApi
  } from "../../services/coreService";
  import StatusModal from "../modal/StatusModal";
  import ButtonSpinner from "../common/ButtonSpinner";
  import Select from 'react-select';
  import { useTranslation } from "react-i18next";
  import { optimizeImageFileForUpload } from "../../utils/imageUploadOptimizer";
  import styles from "../css/Profile.module.css";

  const selectStyles = {
    container: base => ({ ...base, width: '100%' }),
    control: (base, state) => ({
      ...base,
      minHeight: 42,
      borderRadius: 0,
      border: state.isFocused ? '1px solid #475569' : '1px solid #e2e8f0',
      background: '#fff',
      fontSize: 14,
      fontWeight: 500,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(71, 85, 105, 0.1)' : 'none',
      paddingLeft: 24,
      paddingRight: 4
    }),
    menu: base => ({
      ...base,
      borderRadius: 0,
      overflow: 'hidden',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
    })
  };

  export default function Profile() {
    const { t } = useTranslation();
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const getInitials = (name) => {
      if (!name) return "U";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    const [profile, setProfile] = useState({
      name: user.name || '',
      phone: user.phone || '',
      email: user.email || '',
      gender: user.gender || '',
      dateOfBirth: user.dateOfBirth || '',
      image: user.imageUrl && user.imageUrl !== "null" ? user.imageUrl : ""
    });

    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [popup, setPopup] = useState({ open: false, type: '', message: '' });

    const [imagePreview, setImagePreview] = useState("");
    const [imageBase64, setImageBase64] = useState("");

    const fileInputRef = useRef(null);

    const handleChange = (e) => {
      if (e && e.target) {
        setProfile({ ...profile, [e.target.name]: e.target.value });
      } else if (e && e.name && e.value !== undefined) {
        setProfile({ ...profile, [e.name]: e.value });
      }
    };

    const handleEdit = () => setEditMode(true);

    const handleImageChange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const optimizedFile = await optimizeImageFileForUpload(file, {
            maxWidthOrHeight: 1200,
            maxBytes: 320 * 1024,
          });

          const reader = new FileReader();
          reader.onload = (ev) => {
            setImagePreview(ev.target.result);
            const commaIdx = ev.target.result.indexOf(',');
            if (commaIdx !== -1) {
              setImageBase64(ev.target.result.substring(commaIdx + 1));
            }
          };
          reader.readAsDataURL(optimizedFile);
        } catch (error) {
          console.error("Failed to optimize profile image:", error);
          const reader = new FileReader();
          reader.onload = (ev) => {
            setImagePreview(ev.target.result);
            const commaIdx = ev.target.result.indexOf(',');
            if (commaIdx !== -1) {
              setImageBase64(ev.target.result.substring(commaIdx + 1));
            }
          };
          reader.readAsDataURL(file);
        }
      }
    };

    const handlePhotoIconClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleSave = async (e) => {
      e && e.preventDefault();
      setSaving(true);
      setErrorMsg("");
      setSuccessMsg("");
      try {
        let imageUrl = profile.image || "";
        if (imageBase64) {
          const payload = {
            userId: user.spId,
            base64Image: imageBase64
          };
          const res = await uploadProfileImageApi(payload);
          imageUrl = res.data.responseData;
        }
        const now = new Date().toISOString();
        const updatePayload = {
          spId: user.spId,
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          address: user.address || "",
          role: user.role || "SP",
          verified: user.verified !== undefined ? user.verified : true,
          imageUrl: imageUrl,
          gender: profile.gender,
          dateOfBirth: profile.dateOfBirth,
          createdAt: user.createdAt || now,
          updatedAt: now
        };

        await updateUserProfileApi(updatePayload);

        localStorage.setItem("user", JSON.stringify({ ...user, ...updatePayload }));
        setSuccessMsg(t("Profile.updated_success", "Profile updated successfully!"));
        setEditMode(false);
        setPopup({ open: true, type: 'success', message: t("Profile.updated_success", "Profile updated successfully!") });
        setImagePreview("");
        setImageBase64("");
        setProfile(prev => ({ ...prev, image: imageUrl }));
      } catch {
        setErrorMsg(t("Profile.failed_update", "Failed to update profile. Please try again."));
        setPopup({ open: true, type: 'error', message: t("Profile.failed_update", "Failed to update profile. Please try again.") });
      }
      setSaving(false);
      setTimeout(() => {
        setSuccessMsg("");
        setErrorMsg("");
      }, 3000);
    };

    const handleCancel = () => {
      setProfile({
        name: user.name || '',
        phone: user.phone || '',
        email: user.email || '',
        gender: user.gender || '',
        dateOfBirth: user.dateOfBirth || '',
        image: user.imageUrl && user.imageUrl !== "null" ? user.imageUrl : ""
      });
      setEditMode(false);
      setErrorMsg("");
      setSuccessMsg("");
      setImagePreview("");
      setImageBase64("");
    };

    return (
      <div className={styles.pageContainer}>
        <AppHeader
          title={t("Profile.profile", "Profile")}
          showBack={true}
        />

        {/* Header Banner with Floating Circles */}
        <div className={styles.headerBanner}>
          <div className={`${styles.floatingCircle} ${styles.circle1}`} />
          <div className={`${styles.floatingCircle} ${styles.circle2}`} />
          <div className={`${styles.floatingCircle} ${styles.circle3}`} />
        </div>

        <div className={styles.contentWrapper}>
          <div className={styles.profileCard}>
            {!editMode && (
              <button
                className={styles.editButton}
                onClick={handleEdit}
                title={t("Profile.edit_profile", "Edit Profile")}
              >
                <FaPen size={15} className={styles.editIcon} />
              </button>
            )}

            {/* Avatar Section */}
            <div className={styles.avatarSection}>
              <div className={styles.avatarWrapper}>
                {!editMode && <div className={styles.avatarGlow} />}
                <div className={styles.avatar}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" />
                  ) : profile.image ? (
                    <img
                      src={profile.image}
                      alt="User"
                      onError={e => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name || 'User')}&background=475569&color=fff&rounded=true`;
                      }}
                    />
                  ) : (
                    <span>{getInitials(profile.name)}</span>
                  )}
                </div>
                
                {editMode && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className={styles.fileInput}
                      onChange={handleImageChange}
                    />
                    <div
                      className={styles.cameraButton}
                      onClick={handlePhotoIconClick}
                      title={t("Profile.change_photo", "Change profile photo")}
                    >
                      <MdPhotoCamera size={16} className={styles.cameraIcon} />
                    </div>
                  </>
                )}
              </div>

              <div className={styles.userInfo}>
                <h2 className={styles.userName}>{profile.name || "User"}</h2>
                <span className={styles.userRole}>
                  {t("Profile.service_provider", "Service Provider")}
                </span>
              </div>
            </div>

            {/* Form Section */}
            <form onSubmit={handleSave} className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionDot} />
                <h3 className={styles.sectionTitle}>
                  {t("Profile.personal_info", "Personal Information")}
                </h3>
              </div>

              {editMode && (
                <div className={styles.infoCard}>
                  <div className={styles.infoIconWrapper}>
                    <FaInfoCircle size={14} color="#fff" />
                  </div>
                  <p className={styles.infoText}>
                    {t("Profile.edit_info", "Update your personal details below. Changes will be saved to your account.")}
                  </p>
                </div>
              )}

              <div className={styles.fieldGrid}>
                <ProfileField
                  icon={<FaUser size={14} />}
                  label={t("Profile.name", "NAME")}
                  name="name"
                  value={profile.name}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder={t("Profile.enter_name", "Enter your full name")}
                />

                <ProfileField
                  icon={<FaPhone size={14} />}
                  label={t("Profile.phone", "PHONE NUMBER")}
                  name="phone"
                  value={profile.phone}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder={t("Profile.enter_phone", "Enter your phone number")}
                />

                <ProfileField
                  icon={<FaEnvelope size={14} />}
                  label={t("Profile.email", "EMAIL ADDRESS")}
                  name="email"
                  value={profile.email}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder={t("Profile.enter_email", "Enter your email")}
                />

                <ProfileField
                  icon={<FaVenusMars size={14} />}
                  label={t("Profile.gender", "GENDER")}
                  name="gender"
                  value={profile.gender}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder={t("Profile.select_gender", "Select gender")}
                />

                <ProfileField
                  icon={<FaBirthdayCake size={14} />}
                  label={t("Profile.dob", "DATE OF BIRTH")}
                  name="dateOfBirth"
                  value={profile.dateOfBirth}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder={t("Profile.select_dob", "Select date of birth")}
                />
              </div>

              {successMsg && (
                <div className={`${styles.message} ${styles.messageSuccess}`}>
                  <FaCheckCircle size={16} />
                  {successMsg}
                </div>
              )}
              {errorMsg && (
                <div className={`${styles.message} ${styles.messageError}`}>
                  <FaExclamationCircle size={16} />
                  {errorMsg}
                </div>
              )}

              {editMode && (
                <div className={styles.buttonGroup}>
                  <button
                    type="submit"
                    disabled={saving}
                    className={styles.saveButton}
                  >
                    {saving ? (
                      <span className={styles.loadingSpinner}>
                        <ButtonSpinner size={16} color="#fff" type="dots" />
                        <span>{t("Profile.saving", "Saving...")}</span>
                      </span>
                    ) : (
                      t("Profile.save_changes", "Save Changes")
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className={styles.cancelButton}
                  >
                    {t("Profile.cancel", "Cancel")}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 60 }}>
          <BottomNav />
        </div>

        <StatusModal
          open={popup.open}
          message={popup.message}
          success={popup.type === "success"}
          onClose={() => setPopup({ open: false, type: '', message: '' })}
        />
      </div>
    );
  }

  function ProfileField({ icon, label, name, value, editMode, onChange, placeholder }) {
    const { t } = useTranslation();

    if (editMode && name === "gender") {
      const options = [
        { value: '', label: t("Profile.select_gender", "Select Gender") },
        { value: 'Male', label: t("Profile.male", "Male") },
        { value: 'Female', label: t("Profile.female", "Female") },
        { value: 'Other', label: t("Profile.other", "Other") }
      ];
      return (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{label}</label>
          <div className={styles.inputWrapper}>
            <div className={styles.inputIcon}>{icon}</div>
            <Select
              options={options}
              value={options.find(opt => opt.value === value)}
              onChange={opt => onChange({ name, value: opt ? opt.value : '' })}
              styles={selectStyles}
              placeholder={placeholder}
              isSearchable={false}
              menuPlacement="auto"
            />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.fieldGroup}>
        <label className={styles.label}>{label}</label>
        <div className={styles.inputWrapper}>
          <div className={styles.inputIcon}>{icon}</div>
          {editMode ? (
            <input
              type={name === "dateOfBirth" ? "date" : "text"}
              name={name}
              value={value}
              onChange={onChange}
              className={styles.input}
              placeholder={placeholder}
              max={name === "dateOfBirth" ? new Date().toISOString().split('T')[0] : undefined}
              autoComplete="off"
            />
          ) : (
            <div className={`${styles.input} ${styles.inputReadonly}`}>
              {value || <span className={styles.emptyValue}>{t("Profile.not_set", "Not set")}</span>}
            </div>
          )}
        </div>
      </div>
    );
  }